import { useState, useEffect } from "react";
import {
  Form, Input, Select, Button, Switch, TimePicker, message, Spin, Modal, Grid,
} from "antd";
import {
  createB2BClient, updateB2BClient, get_cities_by_state, get_state_by_country,
} from "../../endpoints/API";
import { fetchCountries } from "../country/FetchCountries";
import MapWithPolygon from "../map/Map";
import dayjs from "dayjs";
import GoogleMapsProvider from "../map/GoogleMapsProvider";

const { useBreakpoint } = Grid;

const CreateB2BClientModal = ({ open, onClose, initialData = null, onSuccess }) => {
  const [form] = Form.useForm();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [polygonCoordinates, setPolygonCoordinates] = useState([]);
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);

  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const isEditMode = Boolean(initialData);

  const fetchStatesByCountry = async (countryId) => {
    const res = await get_state_by_country(countryId);
    const data = res?.data || [];
    setStates(data.filter((d) => d.status === "enable").map((d) => ({ value: d.id, label: d.name })));
  };

  const fetchCitiesByState = async (stateId) => {
    const res = await get_cities_by_state(stateId);
    const data = res?.data || [];
    setCities(data.filter((d) => d.status === "enable").map((d) => ({ value: d.id, label: d.name })));
  };

  const handleCountryChange = (countryId) => {
    form.setFieldsValue({ state: undefined, city: undefined });
    setStates([]);
    setCities([]);
    fetchStatesByCountry(countryId);
  };

  const handleStateChange = (stateId) => {
    form.setFieldsValue({ city: undefined });
    setCities([]);
    fetchCitiesByState(stateId);
  };

  const handlePolygonSave = (coords) => {
    const formattedPolygon = {
      type: "Polygon",
      coordinates: [
        [...coords.map((point) => [point.lng, point.lat]), [coords[0].lng, coords[0].lat]],
      ],
    };
    setPolygonCoordinates(formattedPolygon);
    setIsMapModalOpen(false);
  };

  const handleSubmit = async (values) => {
    const checkInTime = dayjs.isDayjs(values?.check_in_time)
      ? values.check_in_time.format("HH:mm:ss")
      : null;
    const checkOutTime = dayjs.isDayjs(values?.check_out_time)
      ? values.check_out_time.format("HH:mm:ss")
      : null;
    const payload = {
      ...values,
      check_in_time: checkInTime,
      check_out_time: checkOutTime,
      office_location: {
        apartment_road_area: values.apartment_road_area,
        house_flat_floor_no: values.house_flat_floor_no,
        status: values.status,
        address_identifier: values.address_identifier,
        state: values.state,
        city: values.city,
        country: values.country,
      },
      service_area: polygonCoordinates?.length === 0 ? null : polygonCoordinates,
    };

    setIsSubmitting(true);
    try {
      const res = isEditMode
        ? await updateB2BClient(initialData.id, payload)
        : await createB2BClient(payload);

      if (res.status === 200 || res.status === 201) {
        message.success(isEditMode ? "Client updated" : "Client created");
        form.resetFields();
        onSuccess?.();
        onClose();
      }
    } catch (err) {
      console.log(err);
      message.error(err?.response?.data?.error || "Submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    fetchCountries(setCountries);
  }, []);

  useEffect(() => {
    if (initialData) {
      const loc = initialData.office_location || {};
      form.setFieldsValue({
        ...initialData,
        ...loc,
        country: loc.country,
        state: loc.state,
        city: loc.city,
        check_in_time: initialData.check_in_time
          ? dayjs(initialData.check_in_time, "HH:mm:ss")
          : null,
        check_out_time: initialData.check_out_time
          ? dayjs(initialData.check_out_time, "HH:mm:ss")
          : null,
      });

      if (initialData.service_area_coords && Array.isArray(initialData.service_area_coords)) {
        setPolygonCoordinates({
          type: "Polygon",
          coordinates: [initialData.service_area_coords],
        });
      }

      if (loc.country) fetchStatesByCountry(loc.country);
      if (loc.state) fetchCitiesByState(loc.state);
    } else {
      form.resetFields();
      setPolygonCoordinates([]);
    }
  }, [initialData, form]);

  return (
    <Modal
      title={isEditMode ? "Edit B2B Client" : "Create B2B Client"}
      open={open}
      onCancel={onClose}
      footer={null}
      // Full-screen on mobile, fixed width on desktop
      width={isMobile ? "100%" : 700}
      style={isMobile ? { top: 0, margin: 0, maxWidth: "100vw", padding: 0 } : {}}
      bodyStyle={
        isMobile
          ? { padding: "12px 10px", maxHeight: "90vh", overflowY: "auto" }
          : { maxHeight: "80vh", overflowY: "auto" }
      }
    >
      <Form
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
        initialValues={{
          notify_email_client: false,
          notify_whatsapp_patient: false,
          notify_whatsapp_report: false,
        }}
      >
        <Form.Item
          name="name"
          label="Client Name"
          rules={[{ required: true, message: "Client Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact Email"
          name="contact_email"
          rules={[
            { required: true, message: "Please enter the contact email" },
            { type: "email", message: "Enter a valid email address" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Contact Number"
          name="contact_number"
          rules={[
            { required: true, message: "Contact number is required" },
            { pattern: /^[6-9]\d{9}$/, message: "Invalid Indian mobile number" },
          ]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Point of Contact Name"
          name="point_of_contact_name"
          rules={[{ required: true, message: "Contact Name is required" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Point of Contact Email"
          name="point_of_contact_email"
          rules={[{ type: "email", message: "Invalid email format" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Password"
          name="password"
          rules={[
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!isEditMode || value) {
                  return value && value.length >= 6
                    ? Promise.resolve()
                    : Promise.reject("Password must be at least 6 characters");
                }
                return Promise.resolve();
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={isEditMode ? "Leave blank to keep current password" : ""}
          />
        </Form.Item>

        {/* Working Hours — stack vertically on mobile */}
        <div className={isMobile ? "flex flex-col gap-0" : "flex gap-5"}>
          <Form.Item label="Working Hour Start" name="check_in_time">
            <TimePicker format="HH:mm" style={{ width: isMobile ? "100%" : "auto" }} />
          </Form.Item>
          <Form.Item label="Working Hour End" name="check_out_time">
            <TimePicker format="HH:mm" style={{ width: isMobile ? "100%" : "auto" }} />
          </Form.Item>
        </div>

        {/* Notification toggles — wrap on mobile */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: isMobile ? "8px 24px" : "0 20px",
            marginBottom: 8,
          }}
        >
          <Form.Item
            label="Notify Email Client"
            name="notify_email_client"
            valuePropName="checked"
            style={{ marginBottom: isMobile ? 8 : 24 }}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Notify WhatsApp Patient"
            name="notify_whatsapp_patient"
            valuePropName="checked"
            style={{ marginBottom: isMobile ? 8 : 24 }}
          >
            <Switch />
          </Form.Item>
          <Form.Item
            label="Notify WhatsApp Report"
            name="notify_whatsapp_report"
            valuePropName="checked"
            style={{ marginBottom: isMobile ? 8 : 24 }}
          >
            <Switch />
          </Form.Item>
        </div>

        <h3 className="mt-6 font-medium">Office Location</h3>

        <Form.Item
          label="Apartment / Road / Area"
          name="apartment_road_area"
          rules={[{ required: true, message: "Please enter Apartment / Road / Area" }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="House / Flat / Floor No."
          name="house_flat_floor_no"
          rules={[{ required: true, message: "Please enter House / Flat / Floor No." }]}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Status"
          name="status"
          rules={[{ required: true, message: "Please select a status" }]}
        >
          <Select
            options={[
              { value: "enable", label: "Enable" },
              { value: "disable", label: "Disable" },
            ]}
          />
        </Form.Item>

        <Form.Item
          label="Address Identifier"
          name="address_identifier"
          rules={[{ required: true, message: "Please enter Address Identifier" }]}
        >
          <Input />
        </Form.Item>

        {/* Country / State / City — stack vertically on mobile */}
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 0 : 20,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <Form.Item
            label="Country"
            name="country"
            style={{ flex: 1, minWidth: 140, width: isMobile ? "100%" : "auto" }}
            rules={[{ required: true, message: "Please select a country" }]}
          >
            <Select
              placeholder="Select Country"
              onChange={handleCountryChange}
              options={countries
                .filter((country) => country.status === "enable")
                .map((country) => ({ value: country.id, label: country.name }))}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="State"
            name="state"
            style={{ flex: 1, minWidth: 140, width: isMobile ? "100%" : "auto" }}
            rules={[{ required: true, message: "Please select a state" }]}
          >
            <Select
              placeholder="Select State"
              onChange={handleStateChange}
              options={states}
              style={{ width: "100%" }}
            />
          </Form.Item>

          <Form.Item
            label="City"
            name="city"
            style={{ flex: 1, minWidth: 140, width: isMobile ? "100%" : "auto" }}
            rules={[{ required: true, message: "Please select a city" }]}
          >
            <Select
              placeholder="Select City"
              options={cities}
              style={{ width: "100%" }}
            />
          </Form.Item>
        </div>

        <h3 className="my-3 font-medium">Service Area</h3>

        <div className="mb-5">
          <Button type="default" onClick={() => setIsMapModalOpen(true)}>
            Select Service Area
          </Button>

          {polygonCoordinates && (
            <div className="mt-3 p-3 bg-gray-100 rounded text-sm max-h-48 overflow-auto">
              <strong>Selected Coordinates:</strong>
              <ul className="list-disc ml-5 mt-2">
                {(polygonCoordinates.coordinates?.[0] || polygonCoordinates).map((coord, index) => (
                  <li key={index}>
                    [{coord[1].toFixed(6)}, {coord[0].toFixed(6)}]
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <Button type="primary" htmlType="submit" disabled={isSubmitting} block={isMobile}>
          {isSubmitting ? (
            <Spin size="small" />
          ) : isEditMode ? (
            "Update Client"
          ) : (
            "Create Client"
          )}
        </Button>
      </Form>

      <GoogleMapsProvider>
        <MapWithPolygon
          isOpen={isMapModalOpen}
          onClose={() => setIsMapModalOpen(false)}
          onSave={handlePolygonSave}
          islat={form.getFieldValue("office_location")?.latitude}
          isLong={form.getFieldValue("office_location")?.longitude}
        />
      </GoogleMapsProvider>
    </Modal>
  );
};

export default CreateB2BClientModal;